import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'

import {
  getCurrentAccountTranslationProvider,
  getCurrentAccountTranslationProviderId,
  getCurrentAccountTranslationProviderSecret,
  getCurrentAccountId,
} from '@SiteModules/Account/selectors'

import { changeTranslationProviderCredentials } from '@SiteModules/Account/actions'

const TRANSLATION_PROVIDERS = [
  'Automatic',
  'LanguageWire'
]

import SearchDropdown from '@SiteComponents/SearchDropdown'
import Button from '@SiteComponents/Button'

import './ProviderSettings.scss'

const ProviderSettings: React.FC = () => {
  const dispatch = useDispatch()
  const providerSettingsRef = useRef<HTMLDivElement>(null)

  const convertTranslationProviderForFrontend = (tp: string) => {
    if (tp === 'automatic') {
      return 'Automatic'
    } else if (tp === 'language_wire') {
      return 'LanguageWire'
    } else {
      return tp
    }
  }

  const convertTranslationProviderForBackend = (tp: string) => {
    if (tp === 'Automatic') {
      return 'automatic'
    } else if (tp === 'LanguageWire') {
      return 'language_wire'
    } else {
      return tp
    }
  }

  const currentAccountId = useSelector(getCurrentAccountId)
  const translationProvider = convertTranslationProviderForFrontend(useSelector(getCurrentAccountTranslationProvider))
  const translationProviderId = useSelector(getCurrentAccountTranslationProviderId)
  const translationProviderSecret = useSelector(getCurrentAccountTranslationProviderSecret)

  const [editedTranslationProvider, setEditedTranslationProvider] = useState(translationProvider)
  const [editedTranslationProviderId, setEditedTranslationProviderId] = useState(translationProviderId)
  const [editedTranslationProviderSecret, setEditedTranslationProviderSecret] = useState(translationProviderSecret)
  const [idIsFocused, setIdIsFocused] = useState(false)
  const [secretIsFocused, setSecretIsFocused] = useState(false)

  const isLanguageWireProvider = editedTranslationProvider === 'LanguageWire'
  const translationProviderIsEdited = editedTranslationProvider !== translationProvider
  const translationProviderIdIsEmpty = !editedTranslationProviderId || editedTranslationProviderId.length === 0
  const translationProviderIdIsEdited = editedTranslationProviderId !== translationProviderId
  const translationProviderSecretIsEmpty = !editedTranslationProviderSecret || editedTranslationProviderSecret.length === 0
  const translationProviderSecretIsEdited = editedTranslationProviderSecret !== translationProviderSecret
  const translationProviderCredentialsAreEdited = translationProviderIdIsEdited || translationProviderSecretIsEdited

  useEffect(() => {
    setEditedTranslationProvider(translationProvider)
  }, [translationProvider])

  useEffect(() => {
    setEditedTranslationProviderId(translationProviderId)
  }, [translationProviderId])

  useEffect(() => {
    setEditedTranslationProviderSecret(translationProviderSecret)
  }, [translationProviderSecret])

  useEffect(() => {
    if (translationProvider === 'Automatic') {
      if (editedTranslationProviderId !== null) {
        setEditedTranslationProviderId(null)
      }

      if (editedTranslationProviderSecret !== null) {
        setEditedTranslationProviderSecret(null)
      }

      if (translationProviderId !== null || translationProviderSecret !== null) {
        dispatch(changeTranslationProviderCredentials({
          translationProviderId: null,
          translationProviderSecret: null,
          translationProvider: convertTranslationProviderForBackend(translationProvider),
          accountId: currentAccountId
        }))
      }
    }
  }, [translationProvider])

  useEffect(() => {
    if (!translationProviderIsEdited || translationProviderCredentialsAreEdited) { return }

    const resetUnsubmittedProviderChange = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const clickIsInsideSettings = providerSettingsRef.current?.contains(target)
      const clickIsInsideDropdown = !!target.closest('.ProviderSettings__translation-dropdown')

      if (!clickIsInsideSettings && !clickIsInsideDropdown) {
        setEditedTranslationProvider(translationProvider)
      }
    }

    document.addEventListener('click', resetUnsubmittedProviderChange)

    return () => {
      document.removeEventListener('click', resetUnsubmittedProviderChange)
    }
  }, [translationProvider, translationProviderIsEdited, translationProviderCredentialsAreEdited])

  const handleSetIdFocus = () => {
    setIdIsFocused(true)
  }

  const handleRemoveIdFocus = () => {
    setIdIsFocused(false)
  }

  const handleSetSecretFocus = () => {
    setSecretIsFocused(true)
  }

  const handleRemoveSecretFocus = () => {
    setSecretIsFocused(false)
  }

  const handleChangeTranslationProvider = (newTranslationProvider: string) => {
    setEditedTranslationProvider(newTranslationProvider)

    if (newTranslationProvider === 'Automatic') {
      dispatch(changeTranslationProviderCredentials({
        translationProviderId: null,
        translationProviderSecret: null,
        translationProvider: convertTranslationProviderForBackend(newTranslationProvider),
        accountId: currentAccountId
      }))
    }
  }

  const handleSetId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newId = (e.target as HTMLInputElement).value
    setEditedTranslationProviderId(newId)
  }

  const handleSetSecret = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSecret = (e.target as HTMLInputElement).value
    setEditedTranslationProviderSecret(newSecret)
  }

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !translationProviderIdIsEmpty && !translationProviderSecretIsEmpty && (idIsFocused || secretIsFocused)) {
      handleSubmitTranslationProviderCredentials()
    }
  }

  const handleSubmitTranslationProviderCredentials = () => {
    dispatch(changeTranslationProviderCredentials({
      translationProviderId: editedTranslationProviderId,
      translationProviderSecret: editedTranslationProviderSecret,
      translationProvider: convertTranslationProviderForBackend(editedTranslationProvider),
      accountId: currentAccountId
    }))
  }

  const renderHeader = () => {
    return (
      <div className="ProviderSettings__header">
        Provider settings
        <div className="ProviderSettings__header-explanation">
          Choose which providers you want to use
        </div>
      </div>
    )
  }

  const renderTranslationProviderInput = () => {
    return (
      <div className="ProviderSettings__provider-input">
        <div
          className="ProviderSettings__provider-dropdown-wrapper"
          id="provider-settings-dropdown-wrapper-translation"
        >
          <SearchDropdown
            availableOptions={TRANSLATION_PROVIDERS}
            selectedOption={editedTranslationProvider as string}
            classNameForClickOutsideListener="ProviderSettings__translation-dropdown"
            idNameOfParentContainer="provider-settings-dropdown-wrapper-translation"
            placeholderText="Select..."
            setSelectedOption={handleChangeTranslationProvider}
            data-testid="translation-provider-select"
          />
          {isLanguageWireProvider && renderCredentialsInput()}
        </div>
      </div>
    )
  }

  const renderCredentialsInput = () => {
    return (
      <div className="ProviderSettings__translation-credentials-inputs">
        <div
          className={classnames(
            'ProviderSettings__translation-credentials-input-wrapper',
            { 'ProviderSettings__translation-credentials-input-wrapper-focused': idIsFocused }
          )}
        >
          <input
            className="ProviderSettings__translation-credentials-input"
            placeholder="Paste Client ID here..."
            onFocus={handleSetIdFocus}
            onBlur={handleRemoveIdFocus}
            autoComplete="off"
            value={editedTranslationProviderId || ''}
            onChange={handleSetId}
            onKeyDown={handleKeydown}
            data-testid="translation-provider-client-id-input"
          />
        </div>
        <div
          className={classnames(
            'ProviderSettings__translation-credentials-input-wrapper',
            { 'ProviderSettings__translation-credentials-input-wrapper-focused': secretIsFocused }
          )}
        >
          <input
            className="ProviderSettings__translation-credentials-input"
            placeholder="Paste Client Secret here..."
            onFocus={handleSetSecretFocus}
            onBlur={handleRemoveSecretFocus}
            autoComplete="off"
            value={editedTranslationProviderSecret || ''}
            onChange={handleSetSecret}
            onKeyDown={handleKeydown}
            data-testid="translation-provider-client-secret-input"
          />
        </div>
        {renderConfirm()}
      </div>
    )
  }

  const renderConfirm = () => {
    const shouldDisableConfirm = translationProviderIdIsEmpty ||
      translationProviderSecretIsEmpty ||
      !(translationProviderIsEdited || translationProviderIdIsEdited || translationProviderSecretIsEdited)

    return (
      <Button
        styleType="brand"
        size="small"
        caption="Confirm"
        onClick={handleSubmitTranslationProviderCredentials}
        disabled={shouldDisableConfirm}
        data-testid="translation-provider-credentials-submit"
      />
    )
  }

  const renderTranslationProvider = () => {
    return (
      <div className="ProviderSettings__provider">
        <div className="ProviderSettings__provider-type">
          Translation
        </div>
        {renderTranslationProviderInput()}
      </div>
    )
  }

  return (
    <div
      className="ProviderSettings"
      ref={providerSettingsRef}
    >
      {renderHeader()}
      {renderTranslationProvider()}
    </div>
  )
}

export default ProviderSettings
